import { useCallback, useEffect, useState } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAddCustomersCommentMutation } from '@clients/store/documents/services';
import { ResponseError, responseErrorsResolver } from '@ui/src';
import { useAppDispatch } from '@store';
import { ApplicationDocument, ApplicationDocumentStatus } from '../../types';
import {
  useBatchDeleteCOFApplicationDocumentsMutation,
  useBatchDeleteCorporateApplicationDocumentsMutation,
  useBatchDeleteIndividualApplicationDocumentsMutation,
  useChangeCOFApplicationDocumentCategoryMutation,
  useChangeCOFApplicationDocumentStatusMutation,
  useChangeCorporateApplicationDocumentCategoryMutation,
  useChangeCorporateApplicationDocumentStatusMutation,
  useChangeIndividualApplicationDocumentCategoryMutation,
  useChangeIndividualApplicationDocumentStatusMutation,
  useChangeNewApplicationDocumentExpirationDateMutation,
  useLazyGetCofApplicationDocumentFileQuery,
  useLazyGetCofNewApplicationDocumentQuery,
  useLazyGetCorporateApplicationDocumentFileQuery,
  useLazyGetCorporateNewApplicationDocumentQuery,
  useLazyGetIndividualApplicationDocumentFileQuery,
  useLazyGetIndividualNewApplicationDocumentQuery,
  useRenameCofDocumentMutation,
  useRenameCorporateDocumentMutation,
  useRenameIndividualDocumentMutation,
} from '../../store/services';
import {
  clearDocumentSidepanelState,
  deleteIsSuccess,
  onDocumentMove,
} from '../../individual/store/slices';
import {
  deleteIsSuccess as corporateDeleteIsSuccess,
  onDocumentMove as corporateOnDocumentMove,
  clearDocumentSidepanelState as corporateClearDocumentSidepanelState,
} from '../../corporate/store/slices';
import {
  deleteIsSuccess as cofDeleteIsSuccess,
  onDocumentMove as cofOnDocumentMove,
  clearDocumentSidepanelState as cofClearDocumentSidepanelState,
} from '../../main-representative/store/slices';

import ROUTES from '../../../../routes';

const useDocument = ({
  isCof,
  isCorporate,
  documentItem,
  documents,
}: {
  isCof?: boolean;
  isCorporate?: boolean;
  documentItem?: ApplicationDocument;
  documents?: ApplicationDocument[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [imgLink, setImgLink] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [nameError, setNameError] = useState('');
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [changeDateError, setChangeDateError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<
    ApplicationDocumentStatus | undefined
  >(undefined);

  const { applicationId, documentId } = useParams();
  const { search } = useLocation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    setError,
    formState: { errors },
  } = useForm();

  const [addComment, { isLoading: commentInProgress }] =
    useAddCustomersCommentMutation();

  const [getDocument, { isLoading: documentIsLoading }] =
    useLazyGetIndividualNewApplicationDocumentQuery();
  const [getDocumentFile, { isLoading, isFetching }] =
    useLazyGetIndividualApplicationDocumentFileQuery();
  const [rename, { isLoading: renameInProgress }] =
    useRenameIndividualDocumentMutation();
  const [
    changeIndividualDocumentStatus,
    { isLoading: statusChangeInProgress, isSuccess },
  ] = useChangeIndividualApplicationDocumentStatusMutation();
  const [changeIndividualDocumentCategory, { isLoading: moveInProgress }] =
    useChangeIndividualApplicationDocumentCategoryMutation();
  const [
    deleteIndividualAppicationDocuments,
    {
      isLoading: deleteInProgress,
      isSuccess: deleteIndividualDocumentIsSuccess,
    },
  ] = useBatchDeleteIndividualApplicationDocumentsMutation();
  const [changeDocumentExpDate, { isLoading: updateInProgress }] =
    useChangeNewApplicationDocumentExpirationDateMutation();
  const [getCorporateDocument, { isLoading: corporateDocumentIsLoading }] =
    useLazyGetCorporateNewApplicationDocumentQuery();
  const [
    getCorporateDocumentFile,
    {
      isLoading: corporateDocumentFileIsLoading,
      isFetching: corporateDocumentFileIsFetching,
    },
  ] = useLazyGetCorporateApplicationDocumentFileQuery();
  const [
    changeCorporateDocumentStatus,
    {
      isLoading: corporateStatusChangeInProgress,
      isSuccess: corporateIsSuccess,
    },
  ] = useChangeCorporateApplicationDocumentStatusMutation();
  const [
    changeCorporateDocumentCategory,
    { isLoading: corporateMoveInProgress },
  ] = useChangeCorporateApplicationDocumentCategoryMutation();
  const [
    deleteCorporateAppicationDocuments,
    {
      isLoading: corporateDeleteInProgress,
      isSuccess: deleteCorporateDocumentIsSuccess,
    },
  ] = useBatchDeleteCorporateApplicationDocumentsMutation();
  const [corporateRename, { isLoading: corporateRenameInProgress }] =
    useRenameCorporateDocumentMutation();
  const [getCOFDocument, { isLoading: cofDocumentIsLoading }] =
    useLazyGetCofNewApplicationDocumentQuery();
  const [
    getCOFDocumentFile,
    {
      isLoading: cofDocumentFileIsLoading,
      isFetching: cofDocumentFileIsFetching,
    },
  ] = useLazyGetCofApplicationDocumentFileQuery();
  const [cofRename, { isLoading: cofRenameInProgress }] =
    useRenameCofDocumentMutation();
  const [
    changeCofDocumentStatus,
    { isLoading: cofStatusChangeInProgress, isSuccess: cofIsSuccess },
  ] = useChangeCOFApplicationDocumentStatusMutation();
  const [changeCofDocumentCategory, { isLoading: cofMoveInProgress }] =
    useChangeCOFApplicationDocumentCategoryMutation();
  const [
    deleteCofAppicationDocuments,
    { isLoading: cofDeleteInProgress, isSuccess: deleteCofDocumentIsSuccess },
  ] = useBatchDeleteCOFApplicationDocumentsMutation();

  const changeDocumentStatus = async (status: ApplicationDocumentStatus) => {
    if (isCof) {
      changeCofDocumentStatus({
        id: applicationId as string,
        body: {
          ids: [documentItem?.id as string],
          status,
        },
      });
    } else if (isCorporate) {
      changeCorporateDocumentStatus({
        id: applicationId as string,
        body: {
          ids: [documentItem?.id as string],
          status,
        },
      });
    } else {
      changeIndividualDocumentStatus({
        id: applicationId as string,
        body: {
          ids: [documentItem?.id as string],
          status,
        },
      });
    }
  };

  const getDocumentFileAction = async () => {
    let response;

    if (isCof) {
      response = await getCOFDocumentFile({
        id: applicationId as string,
        documentId: documentId as string,
      });
    } else if (isCorporate) {
      response = await getCorporateDocumentFile({
        id: applicationId as string,
        documentId: documentId as string,
      });
    } else {
      response = await getDocumentFile({
        id: applicationId as string,
        documentId: documentId as string,
      });
    }

    if (response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onRename = async () => {
    if (documentName.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      let response;

      if (isCof) {
        response = (await cofRename({
          id: applicationId as string,
          documentId: documentId as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      } else if (isCorporate) {
        response = (await corporateRename({
          id: applicationId as string,
          documentId: documentId as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      } else {
        response = (await rename({
          id: applicationId as string,
          documentId: documentId as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      }

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const onDownload = () => {
    if (imgLink) {
      const hiddenElement = document.createElement('a');
      // @ts-ignore
      hiddenElement.href = imgLink;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${documentItem?.name}`;
      hiddenElement.click();
    }
  };

  const addCommentAction = (text: string) => {
    addComment({
      id: documentId as string,
      body: {
        text,
      },
    });
  };

  const onDelete = async () => {
    let response;

    if (isCof) {
      response = await deleteCofAppicationDocuments({
        id: applicationId as string,
        documentId: [documentId as string],
      });
    } else if (isCorporate) {
      response = await deleteCorporateAppicationDocuments({
        id: applicationId as string,
        documentId: [documentId as string],
      });
    } else {
      response = await deleteIndividualAppicationDocuments({
        id: applicationId as string,
        documentId: [documentId as string],
      });
    }

    if (response) {
      setDeletePopupVisible(false);

      const nextDocuments = documents?.filter((item) => item.id !== documentId);

      if (isCof) {
        dispatch(cofDeleteIsSuccess(nextDocuments as ApplicationDocument[]));
      } else if (isCorporate) {
        dispatch(
          corporateDeleteIsSuccess(nextDocuments as ApplicationDocument[]),
        );
      } else {
        dispatch(deleteIsSuccess(nextDocuments as ApplicationDocument[]));
      }
    }
  };

  const onChangeDocumentExpDate = async (date: string) => {
    const response = (await changeDocumentExpDate({
      id: documentId as string,
      body: {
        id: documentId as string,
        expirationDate: date,
      },
    })) as { error?: ResponseError | { data: { message: string } } };

    if (response.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setChangeDateError(
            response?.error?.data?.message[0]?.value as string,
          );
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setChangeDateError(response?.error?.data?.message);
      } else {
        setChangeDateError('Internal Server Error. Try Again');
      }
    }
  };

  const changeDocumentCategoryAction = async (documentCategoryId: string) => {
    let response;

    if (isCof) {
      response = await changeCofDocumentCategory({
        id: applicationId as string,
        body: {
          ids: [documentId as string],
          documentCategoryId,
        },
      });
    } else if (isCorporate) {
      response = await changeCorporateDocumentCategory({
        id: applicationId as string,
        body: {
          ids: [documentId as string],
          documentCategoryId,
        },
      });
    } else {
      response = await changeIndividualDocumentCategory({
        id: applicationId as string,
        body: {
          ids: [documentId as string],
          documentCategoryId,
        },
      });
    }

    if (response) {
      if (isCof) {
        dispatch(cofOnDocumentMove(documentCategoryId));
      } else if (isCorporate) {
        dispatch(corporateOnDocumentMove(documentCategoryId));
      } else {
        dispatch(onDocumentMove(documentCategoryId));
      }
    }
  };

  const getDocumentAction = () => {
    if (isCof) {
      getCOFDocument({
        id: applicationId as string,
        documentId: documentId as string,
      });
    } else if (isCorporate) {
      getCorporateDocument({
        id: applicationId as string,
        documentId: documentId as string,
      });
    } else {
      getDocument({
        id: applicationId as string,
        documentId: documentId as string,
      });
    }

    getDocumentFileAction();
  };

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setDocumentName('');
    setNameError('');

    setTimeout(() => {
      let linkString;

      if (isCof) {
        dispatch(cofClearDocumentSidepanelState());
        linkString = generatePath(
          ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL,
          {
            applicationId: applicationId as string,
          },
        );
      } else if (isCorporate) {
        dispatch(corporateClearDocumentSidepanelState());
        linkString = generatePath(
          ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO,
          {
            applicationId: applicationId as string,
          },
        );
      } else {
        linkString = generatePath(
          ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO,
          {
            applicationId: applicationId as string,
          },
        );
        dispatch(clearDocumentSidepanelState());
      }

      navigate(`${linkString}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    if (documentItem) {
      setDocumentName(documentItem.name);
    }
  }, [documentItem]);

  useEffect(() => {
    if (isSuccess || cofIsSuccess || corporateIsSuccess) {
      setSelectedStatus(undefined);
    }
  }, [isSuccess, cofIsSuccess, corporateIsSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (documentId && applicationId) {
      getDocumentAction();
    }
  }, [dispatch, documentId, applicationId]);

  useEffect(() => {
    if (
      deleteCofDocumentIsSuccess ||
      deleteIndividualDocumentIsSuccess ||
      deleteCorporateDocumentIsSuccess
    ) {
      handleClose();
    }
  }, [
    deleteCofDocumentIsSuccess,
    deleteIndividualDocumentIsSuccess,
    deleteCorporateDocumentIsSuccess,
  ]);

  return {
    documentIsLoading:
      documentIsLoading || cofDocumentIsLoading || corporateDocumentIsLoading,
    isLoading:
      isLoading || corporateDocumentFileIsLoading || cofDocumentFileIsLoading,
    onDownload,
    isFetching:
      isFetching ||
      corporateDocumentFileIsFetching ||
      cofDocumentFileIsFetching,
    imgLink,
    addCommentAction,
    commentInProgress,
    setDocumentName,
    documentName,
    nameError,
    errors,
    onChangeDocumentExpDate,
    moveInProgress:
      moveInProgress || cofMoveInProgress || corporateMoveInProgress,
    setDeletePopupVisible,
    changeDocumentCategoryAction,
    changeDateError,
    updateInProgress,
    onDelete,
    deleteInProgress:
      deleteInProgress || cofDeleteInProgress || corporateDeleteInProgress,
    deletePopupVisible,
    panelIsOpen,
    handleClose,
    renameInProgress:
      renameInProgress || cofRenameInProgress || corporateRenameInProgress,
    onRename,
    statusChangeInProgress:
      statusChangeInProgress ||
      cofStatusChangeInProgress ||
      corporateStatusChangeInProgress,
    selectedStatus,
    setSelectedStatus,
    changeDocumentStatus,
  };
};

export default useDocument;
